import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listIntake, createIntake, updateIntake, resolveIntakePlace,
  importIntake, deleteIntake, deleteClient, listClients, getIntakeLfKeywords,
  createIntakeCampaign, startIntakeCampaign, createAllCampaigns, syncIntakeCampaignKeywords, listPeOptions,
  reconcileIntake, startIntakeSetup, getOnboardingJob,
  type ImportIntakeResponse, type IntakePatch, type PeOption, type ReconcileRow, type OnboardingJob,
} from '../lib/api';
import type { IntakeRow, Client } from '../types';
import { useAuth } from '../lib/auth';
import { useIsMobile } from '../lib/useIsMobile';
import { GBIBadge, GBIBtn, T, sharedStyles } from '../components/Shared';

type Tab = 'form' | 'data' | 'reconcile';

const LANGS = [
  { v: 'en', label: 'English' },
  { v: 'zh', label: '中文 (Chinese)' },
  { v: 'ms', label: 'Bahasa Malaysia' },
] as const;
const LANG_LABEL: Record<string, string> = { en: 'English', zh: '中文', ms: 'BM' };

// Target markets — multi-select (a business can serve both MY and SG).
const MARKETS = [
  { v: 'MY', label: 'Malaysia (MY)' },
  { v: 'SG', label: 'Singapore (SG)' },
] as const;

const STATUS_COLOR: Record<IntakeRow['status'], string> = {
  draft: T.fgMuted,
  ready: T.cyan,
  imported: '#1E9E5A',
  archived: T.borderBright,
};

// Short, human label for a record — the clean Client Name, never the long GBP.
function displayName(r: IntakeRow): string {
  return (r.clientName || r.gbpName || 'Untitled').trim();
}

// ── CSV: one client = its primary row + linked branch rows ─────────────────
function groupMembers(row: IntakeRow, all: IntakeRow[]): IntakeRow[] {
  const rootId = row.parentIntakeId || row.id;
  const root = all.find(r => r.id === rootId) ?? row;
  const branches = all.filter(r => r.parentIntakeId === rootId && r.id !== rootId);
  return [root, ...branches];
}
// Sidebar order: each primary followed immediately by its linked branch rows,
// so two same-named records (one per location) read as a clear parent → child
// group instead of being scattered by updatedAt.
function orderBySibling(all: IntakeRow[]): IntakeRow[] {
  const ordered: IntakeRow[] = [];
  for (const head of all.filter(r => !r.parentIntakeId)) {
    ordered.push(head);
    for (const b of all) if (b.parentIntakeId === head.id) ordered.push(b);
  }
  for (const r of all) if (!ordered.includes(r)) ordered.push(r); // orphan branches
  return ordered;
}
function csvEscape(v: unknown): string {
  return `"${String(v ?? '').replace(/"/g, '""')}"`;
}
function exportClientCsv(row: IntakeRow, all: IntakeRow[]) {
  const members = groupMembers(row, all);
  const head = [
    'Role', 'Client Name', 'GBP Name', 'Location / Area', 'Country', 'Target Markets', 'Google Map Link',
    'Place ID', 'Confirmed Name', 'Confirmed Address',
    'Keywords', 'Keyword Languages', 'AI Prompts', 'Client Emails', 'Branch Label', 'Status',
  ];
  const lines = members.map((m, i) => [
    i === 0 ? 'Primary' : 'Branch',
    m.clientName, m.gbpName, m.locationArea, m.country, m.targetMarkets.join(' | '), m.googleMapLink,
    m.placeId, m.placeConfirmName, m.placeConfirmAddress,
    m.keywords.join(' | '),
    m.keywordLanguages.map(l => LANG_LABEL[l] ?? l).join(' | '),
    (m.aiPromptsSame ? m.keywords : m.aiPrompts).join(' | '),
    m.clientEmails.join(' | '), m.branchLabel, m.status,
  ].map(csvEscape).join(','));
  const csv = [head.map(csvEscape).join(','), ...lines].join('\n');
  const slug = (displayName(members[0])).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `localfalcon-${slug || 'client'}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ──────────────────────────────────────────────────────────────

const DISCARD_MSG = 'You have unsaved changes that will be lost. Continue?';

export function AdminIntake() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canStartSetup = user?.role === 'admin' || user?.role === 'manager';
  const recoveryMode = isAdmin && new URLSearchParams(window.location.search).get('recovery') === '1';

  const [tab, setTab] = useState<Tab>('form');
  const [rows, setRows] = useState<IntakeRow[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  // Lifted from the editor so record/tab navigation can warn before discarding.
  const [dirty, setDirty] = useState(false);
  const isMobile = useIsMobile();
  const guardDiscard = () => !dirty || confirm(DISCARD_MSG);
  const selectRecord = (id: string) => { if (guardDiscard()) { setDirty(false); setSelectedId(id); } };

  const reload = async (selectAfter?: string) => {
    try {
      const list = await listIntake();
      setRows(list);
      if (selectAfter) setSelectedId(selectAfter);
      else if (list.length && !list.some(r => r.id === selectedId)) {
        setSelectedId(list[0]?.id ?? null);
      }
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, []);

  const selected = rows?.find(r => r.id === selectedId) ?? null;
  const patchLocal = (row: IntakeRow) =>
    setRows(prev => (prev ? prev.map(r => (r.id === row.id ? row : r)) : prev));

  const handleNew = async () => {
    if (!guardDiscard()) return;
    setDirty(false);
    const row = await createIntake();
    setRows(prev => (prev ? [row, ...prev] : [row]));
    setSelectedId(row.id);
    setTab('form');
  };

  const importedCount = rows?.filter(r => r.status === 'imported').length ?? 0;

  // Ready rows that don't yet have an LF campaign (mirrors the backend isReady).
  const eligibleForCampaign = (rows ?? []).filter(r =>
    !r.lfCampaignKey && r.status !== 'archived' &&
    !!r.placeId && !!r.gbpName?.trim() && !!r.locationArea?.trim() &&
    r.keywords.some(k => k.trim()));
  const eligibleCount = eligibleForCampaign.length;

  const [batching, setBatching] = useState(false);
  const [batchMsg, setBatchMsg] = useState<string | null>(null);

  // Batch-create paused campaigns for all eligible rows. Server processes a safe
  // chunk per call; we loop until done (stop on no-progress).
  const runBatchCreate = async () => {
    if (batching) return;
    if (!confirm(
      `Create paused Local Falcon campaigns for ${eligibleCount} ready client location(s)?\n\n` +
      `• Creating is FREE — campaigns start PAUSED.\n` +
      `• Each costs ~108 credits per run once you re-center + start it on Local Falcon.\n` +
      `• Already-created and incomplete rows are skipped automatically.`,
    )) return;
    setBatching(true); setBatchMsg('Creating…');
    let created = 0; let linked = 0; const failed: string[] = [];
    try {
      for (let i = 0; i < 15; i++) {
        const res = await createAllCampaigns();
        created += res.created.length;
        linked += res.linked.length;
        failed.push(...res.failed.map(f => f.name));
        setBatchMsg(`Created ${created}, linked ${linked}… ${res.remaining} remaining`);
        // Stop when nothing left, or this batch made no progress (only failures).
        if (res.remaining === 0 || (res.created.length === 0 && res.linked.length === 0)) break;
      }
      await reload();
      setBatchMsg(`✓ Done — ${created} created (paused)` +
        (linked ? `, ${linked} linked to existing (no duplicate)` : '') +
        (failed.length ? ` ⚠️ ${failed.length} failed: ${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '…' : ''}` : '') + '.');
    } catch (e) {
      setBatchMsg('Failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally { setBatching(false); }
  };

  return (
    // Own scroll: global CSS sets body{overflow:hidden}.
    <div style={{ height: '100vh', overflowY: 'auto', background: '#f8f9fa', padding: isMobile ? '20px 14px' : '40px 32px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', paddingBottom: 80 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
          <div>
            <div style={{
              fontFamily: sharedStyles.fontDisplay, fontWeight: 700, fontSize: 11,
              background: T.brandGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6,
            }}>AI Intelligence — Internal Console</div>
            <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 28, fontWeight: 700, color: T.fgPrimary }}>
              Client Intake
            </div>
            <div style={{ fontSize: 13, color: T.fgSecondary, marginTop: 4 }}>
              {rows ? `${rows.length} record${rows.length === 1 ? '' : 's'} · ${importedCount} live client${importedCount === 1 ? '' : 's'}` : 'Loading…'} · stays in sync with the Clients list
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Link to="/admin" style={{ textDecoration: 'none' }}>
              <button style={ghostBtn}>← Back to Console</button>
            </Link>
            {isAdmin && (
              <Link to="/admin/users" style={{ textDecoration: 'none' }}>
                <button style={ghostBtn}>👥 Users</button>
              </Link>
            )}
            {recoveryMode && eligibleCount > 0 && (
              <GBIBtn variant="secondary" onClick={runBatchCreate} disabled={batching}>
                {batching ? 'Creating…' : `📍 Create ${eligibleCount} campaign${eligibleCount === 1 ? '' : 's'}`}
              </GBIBtn>
            )}
            <GBIBtn variant="primary" onClick={handleNew}>+ New intake</GBIBtn>
          </div>
        </div>

        {batchMsg && (
          <div style={{
            marginBottom: 14, padding: '9px 12px', borderRadius: 8, fontSize: 12.5,
            background: batchMsg.startsWith('Failed') ? 'rgba(232,80,26,0.08)' : 'rgba(18,181,196,0.08)',
            border: `1px solid ${batchMsg.startsWith('Failed') ? '#E8501A' : T.cyan}`,
            color: T.fgPrimary, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          }}>
            <span>{batchMsg}</span>
            <button onClick={() => setBatchMsg(null)} style={{ ...ghostBtn, padding: '2px 8px', fontSize: 11 }}>Dismiss</button>
          </div>
        )}

        {user && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            gap: 12, marginBottom: 16, fontSize: 12, color: T.fgMuted,
          }}>
            <span>Signed in as <strong style={{ color: T.fgPrimary }}>{user.email}</strong> ({user.role})</span>
            <button onClick={logout} style={{ ...ghostBtn, padding: '4px 10px', fontSize: 11 }}>Sign out</button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 22 }}>
          {(['form', 'data', 'reconcile'] as Tab[]).map(t => (
            <button key={t} onClick={() => { if (tab === 'form' && t !== 'form' && !guardDiscard()) return; if (t !== 'form') setDirty(false); setTab(t); }} style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '10px 18px', fontSize: 13, fontWeight: 600, fontFamily: sharedStyles.fontBody,
              color: tab === t ? T.cyan : T.fgSecondary,
              borderBottom: `2px solid ${tab === t ? T.cyan : 'transparent'}`, marginBottom: -1,
            }}>
              {t === 'form' ? 'Form' : t === 'data' ? `Data view${rows ? ` (${rows.length})` : ''}` : '📊 Reconcile (LF vs Dashboard)'}
            </button>
          ))}
        </div>

        {loadErr && <ErrorBox msg={loadErr} />}

        {tab === 'form' && (
          <FormTab
            rows={rows} selected={selected}
            onSelect={selectRecord} onNew={handleNew}
            onLocalPatch={patchLocal} onReload={reload} isAdmin={isAdmin}
            canStartSetup={canStartSetup} recoveryMode={recoveryMode}
            onDirtyChange={setDirty}
          />
        )}
        {tab === 'data' && rows && (
          <DataTab rows={rows} onOpen={(id) => { setDirty(false); setSelectedId(id); setTab('form'); }} />
        )}
        {tab === 'reconcile' && (
          <ReconcileTab onOpen={(id) => { setDirty(false); setSelectedId(id); setTab('form'); }} />
        )}
      </div>
    </div>
  );
}

// ── Tab 1 — Form ───────────────────────────────────────────────

function FormTab({ rows, selected, onSelect, onNew, onLocalPatch, onReload, isAdmin, canStartSetup, recoveryMode, onDirtyChange }: {
  rows: IntakeRow[] | null;
  selected: IntakeRow | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onLocalPatch: (r: IntakeRow) => void;
  onReload: (selectAfter?: string) => Promise<void>;
  isAdmin: boolean;
  canStartSetup: boolean;
  recoveryMode: boolean;
  onDirtyChange: (d: boolean) => void;
}) {
  const isMobile = useIsMobile();
  // Selectable Project Executives (active managers). Fetched once here so it
  // survives record switches; passed down to each editor.
  const [peOptions, setPeOptions] = useState<PeOption[]>([]);
  useEffect(() => { listPeOptions().then(setPeOptions).catch(() => setPeOptions([])); }, []);

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | IntakeRow['status']>('');

  const counts = useMemo(() => {
    const c = { total: 0, draft: 0, ready: 0, imported: 0, archived: 0 } as Record<string, number>;
    for (const r of rows ?? []) { c.total++; c[r.status] = (c[r.status] ?? 0) + 1; }
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return orderBySibling(rows ?? []).filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!s) return true;
      return [r.clientName, r.gbpName, r.locationArea, r.branchLabel, r.placeId, ...r.clientEmails, ...r.keywords]
        .some(v => v?.toLowerCase().includes(s));
    });
  }, [rows, q, statusFilter]);

  if (!rows) return <div style={{ color: T.fgMuted, fontSize: 13 }}>Loading…</div>;

  const STATUS_ORDER: IntakeRow['status'][] = ['ready', 'draft', 'imported', 'archived'];
  const pills = [{ key: '', label: 'All', n: counts.total }, ...STATUS_ORDER.filter(s => counts[s] > 0).map(s => ({ key: s, label: s.charAt(0).toUpperCase() + s.slice(1), n: counts[s] }))];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: 20, alignItems: 'start' }}>
      <div style={{ position: 'sticky', top: 0 }}>
        <div style={{ ...labelStyle, marginBottom: 8 }}>Intake records</div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search records..."
          style={{ ...inp, marginBottom: 8 }} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
          {pills.map(p => {
            const active = statusFilter === p.key;
            const accent = p.key ? STATUS_COLOR[p.key as IntakeRow['status']] : T.cyan;
            return (
              <button key={p.key || 'all'} onClick={() => setStatusFilter(p.key as '' | IntakeRow['status'])}
                style={{
                  padding: '3px 9px', borderRadius: 9999, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${active ? accent : T.border}`,
                  background: active ? accent + '18' : '#fff',
                  color: active ? accent : T.fgSecondary,
                }}>
                {p.label} {p.n}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '64vh', overflowY: 'auto' }}>
          {rows.length === 0 && (
            <div style={{ fontSize: 12, color: T.fgMuted, fontStyle: 'italic', padding: 8 }}>
              No records yet. Click “+ New intake”.
            </div>
          )}
          {rows.length > 0 && visible.length === 0 && (
            <div style={{ fontSize: 12, color: T.fgMuted, fontStyle: 'italic', padding: 8 }}>
              No records match.
            </div>
          )}
          {visible.map(r => {
            const on = selected?.id === r.id;
            const isBranchRow = !!r.parentIntakeId;
            return (
              <button key={r.id} onClick={() => onSelect(r.id)} style={{
                textAlign: 'left', padding: '9px 11px', borderRadius: 7, cursor: 'pointer',
                border: `1px solid ${on ? T.cyan : T.border}`,
                background: on ? 'rgba(18,181,196,0.07)' : '#fff',
                marginLeft: isBranchRow ? 10 : 0,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.fgPrimary, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {isBranchRow ? '↳ ' : ''}{displayName(r)}
                </div>
                <div style={{ fontSize: 10, color: T.fgMuted, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📍 {r.branchLabel?.trim() || r.locationArea?.trim() || '—'}
                </div>
                <GBIBadge color={STATUS_COLOR[r.status]}>{r.status}</GBIBadge>
              </button>
            );
          })}
        </div>
        <button onClick={onNew} style={{ ...ghostBtn, width: '100%', marginTop: 10 }}>+ New intake</button>
      </div>

      <div>
        {selected
          ? <IntakeEditor key={selected.id} row={selected} allRows={rows}
              onLocalPatch={onLocalPatch} onReload={onReload} isAdmin={isAdmin}
              canStartSetup={canStartSetup} recoveryMode={recoveryMode}
              onDirtyChange={onDirtyChange} peOptions={peOptions} />
          : <div style={{ color: T.fgMuted, fontSize: 13, padding: 20 }}>Select a record on the left, or create a new one.</div>}
      </div>
    </div>
  );
}

function IntakeEditor({ row, allRows, onLocalPatch, onReload, isAdmin, canStartSetup, recoveryMode, onDirtyChange, peOptions }: {
  row: IntakeRow;
  allRows: IntakeRow[];
  onLocalPatch: (r: IntakeRow) => void;
  onReload: (selectAfter?: string) => Promise<void>;
  isAdmin: boolean;
  canStartSetup: boolean;
  recoveryMode: boolean;
  onDirtyChange: (d: boolean) => void;
  peOptions: PeOption[];
}) {
  const [form, setForm] = useState<IntakeRow>(row);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirtyState] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [importResult, setImportResult] = useState<ImportIntakeResponse | null>(null);
  const [kwDrag, setKwDrag] = useState<number | null>(null);   // keyword being dragged
  const [clients, setClients] = useState<Client[] | null>(null);
  const [parentQuery, setParentQuery] = useState('');
  const [setupJob, setSetupJob] = useState<OnboardingJob | null>(null);
  const setupKeyRef = useRef(`${row.id}:${crypto.randomUUID()}`);

  // One source of truth for "unsaved changes" — also bubbled up so the parent
  // can warn before record/tab navigation.
  const markDirty = (d: boolean) => {
    if (d) setupKeyRef.current = `${row.id}:${crypto.randomUUID()}`;
    setDirtyState(d);
    onDirtyChange(d);
  };

  useEffect(() => { setForm(row); setDirtyState(false); onDirtyChange(false); }, [row]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirtyChange(false), []); // reset on unmount (record switch) — eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!form.isBranch || clients !== null) return;
    listClients().then(setClients).catch(() => setClients([]));
  }, [form.isBranch, clients]);
  // Browser-level guard for tab close / refresh with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  useEffect(() => {
    if (!setupJob || ['completed', 'failed', 'needs_input'].includes(setupJob.status)) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      getOnboardingJob(setupJob.id).then(job => {
        if (!cancelled) setSetupJob(job);
      }).catch(() => { /* keep last state; the next poll retries */ });
    }, 3000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [setupJob?.id, setupJob?.status]);

  // Imported rows stay a living reference: the GBP identity (GBP name, map
  // link, Place ID, category, country) locks; the operational fields stay
  // editable and propagate back to the live client on save.
  const idLock = form.status === 'imported';

  // Local-only edit — nothing is persisted until Save is clicked.
  const set = <K extends keyof IntakeRow>(k: K, v: IntakeRow[K]) => {
    setForm(f => ({ ...f, [k]: v }));
    markDirty(true);
  };

  // One full patch from the current form. The PATCH endpoint drops locked
  // keys for imported rows server-side, so sending everything is safe.
  const buildPatch = (): IntakePatch => ({
    clientName: form.clientName,
    gbpName: form.gbpName,
    locationArea: form.locationArea,
    country: form.country,
    targetMarkets: form.targetMarkets,
    category: form.category,
    clientContactName: form.clientContactName,
    googleMapLink: form.googleMapLink,
    placeId: form.placeId,
    notes: form.notes,
    isBranch: form.isBranch,
    parentClientId: form.parentClientId,
    parentIntakeId: form.parentIntakeId,
    branchLabel: form.branchLabel,
    keywords: form.keywords.map(x => x.trim()).filter(Boolean),
    keywordLanguages: form.keywordLanguages,
    aiPrompts: form.aiPrompts.map(x => x.trim()).filter(Boolean),
    aiPromptsSame: form.aiPromptsSame,
    clientEmails: form.clientEmails.map(x => x.trim()).filter(Boolean),
    lfTrackCount: form.lfTrackCount,
    projectExecutives: form.projectExecutives,
  });

  // Explicit, all-at-once save.
  const saveAll = async (silent = false): Promise<IntakeRow | null> => {
    setSaving(true); setMsg(null);
    try {
      const updated = await updateIntake(row.id, buildPatch());
      setForm(updated);
      onLocalPatch(updated);
      markDirty(false);
      if (!silent) setMsg({ kind: 'ok', text: '✓ Saved' });
      return updated;
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Resolve reads the row server-side, so persist pending edits first.
  const resolve = async () => {
    setBusy('resolve'); setMsg(null);
    try {
      if (dirty && !(await saveAll(true))) { setBusy(null); return; }
      const res = await resolveIntakePlace(row.id);
      setForm(res.row);
      onLocalPatch(res.row);
      markDirty(false);
      setMsg({ kind: 'ok', text: `Matched: ${res.place.displayName ?? '—'} · ${res.place.formattedAddress ?? ''} (${res.place.source})` });
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  // Pull what Local Falcon is actually tracking (Maps keywords + AI prompts)
  // and merge anything missing into the form. Non-destructive (only adds);
  // nothing is persisted until the user reviews and clicks Save.
  const reconcileFromLf = async () => {
    setBusy('reconcile'); setMsg(null);
    try {
      const r = await getIntakeLfKeywords(row.id);
      if (!r.imported) {
        setMsg({ kind: 'err', text: 'This record isn’t a live client yet — nothing in Local Falcon to reconcile.' });
        return;
      }
      const addMissing = (existing: string[], incoming: string[]) => {
        const seen = new Set(existing.map(x => x.trim().toLowerCase()).filter(Boolean));
        const add: string[] = [];
        for (const k of incoming) {
          const key = (k ?? '').trim().toLowerCase();
          if (key && !seen.has(key)) { seen.add(key); add.push(k.trim()); }
        }
        return add;
      };
      const curKw = form.keywords.filter(x => x.trim());
      const addKw = addMissing(curKw, r.mapsKeywords);
      const curAp = form.aiPrompts.filter(x => x.trim());
      const addAp = form.aiPromptsSame ? [] : addMissing(curAp, r.aiKeywords);
      if (!addKw.length && !addAp.length) {
        setMsg({ kind: 'ok', text: 'Already in sync with Local Falcon — nothing to add.' });
        return;
      }
      setForm(f => ({
        ...f,
        keywords: addKw.length ? [...f.keywords.filter(x => x.trim()), ...addKw] : f.keywords,
        aiPrompts: addAp.length ? [...f.aiPrompts.filter(x => x.trim()), ...addAp] : f.aiPrompts,
      }));
      markDirty(true);
      const parts: string[] = [];
      if (addKw.length) parts.push(`${addKw.length} keyword${addKw.length === 1 ? '' : 's'}`);
      if (addAp.length) parts.push(`${addAp.length} AI prompt${addAp.length === 1 ? '' : 's'}`);
      setMsg({ kind: 'ok', text: `Pulled from Local Falcon: added ${parts.join(' + ')}. Review and click Save to keep.` });
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  const doImport = async () => {
    // Convert operates on the saved row — flush pending edits first.
    if (dirty && !(await saveAll(true))) return;
    if (!confirm('Create a live client account from this record? This also runs Places / Local Falcon sync.' + (form.clientEmails.length ? '' : '\n\nNo client email yet — the client is created without a login (add emails later).'))) return;
    setBusy('import'); setMsg(null);
    try {
      const res = await importIntake(row.id);
      setImportResult(res);
      await onReload(row.id);
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  const doStartSetup = async () => {
    setBusy('setup'); setMsg(null);
    try {
      // One user action, two atomic server calls: persist the complete form,
      // then create the durable job. The idempotency key protects retries.
      const saved = await saveAll(true);
      if (!saved) return;
      const started = await startIntakeSetup(row.id, setupKeyRef.current);
      setSetupJob(await getOnboardingJob(started.jobId));
      setMsg({ kind: 'ok', text: 'Saved and started. Automation continues even if this page is closed.' });
      await onReload(row.id);
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  // ① Create the standard LF campaign (paused) for the top-3 keywords.
  const doCreateCampaign = async () => {
    if (dirty && !(await saveAll(true))) return;   // create operates on the saved row
    setBusy('campaign'); setMsg(null);
    try {
      const res = await createIntakeCampaign(row.id);
      setMsg({
        kind: 'ok',
        text: res.alreadyExists
          ? 'Campaign already exists — review it on Local Falcon below.'
          : `Campaign created (paused) — ${res.breakdown.scans} scans / ${res.breakdown.credits} credits per run. Re-center the grid on Local Falcon, then Start.`,
      });
      await onReload(row.id);
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  // ② Resume + run the campaign after the admin reviewed/re-centered it on LF.
  const doStartCampaign = async () => {
    if (!form.lfCampaignKey) return;
    if (!confirm('Start this campaign now? It runs immediately (uses credits) and then repeats every 2 weeks.')) return;
    setBusy('campaign-start'); setMsg(null);
    try {
      await startIntakeCampaign(row.id);
      setMsg({ kind: 'ok', text: 'Campaign started — first run triggered; it now repeats biweekly.' });
      await onReload(row.id);
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  // Push the intake top-N keywords to the linked LF campaign (Intake = source of truth).
  const doSyncKeywords = async () => {
    if (!form.lfCampaignKey) return;
    if (dirty && !(await saveAll(true))) return;   // sync the saved keywords
    if (!confirm('Push this row\'s keywords to its Local Falcon campaign? It replaces the campaign\'s current keywords with your Client Intake list (old keyword history stops; new keywords start fresh).')) return;
    setBusy('campaign-sync'); setMsg(null);
    try {
      const { added, removed } = await syncIntakeCampaignKeywords(row.id);
      setMsg({
        kind: 'ok',
        text: (added.length || removed.length)
          ? `Keywords pushed to Local Falcon — ${added.length ? `added: ${added.join(', ')}` : 'none added'}${removed.length ? ` · removed: ${removed.join(', ')}` : ''}.`
          : 'Already in sync — nothing to change.',
      });
      await onReload(row.id);
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(null); }
  };

  // Shared delete gate (admin-only is enforced separately by the backend and
  // the button visibility). Two steps: (1) the client's full name typed
  // exactly as the "password", then (2) an explicit final confirmation.
  // Returns true only when both pass. `extra` describes what else is wiped.
  const deleteGate = (name: string, extra?: string): boolean => {
    const typed = window.prompt(
      `Deleting is permanent and cannot be undone.\n\n` +
      `Enter the client's full name exactly to unlock the delete:\n\n"${name}"`,
    );
    if (typed === null) return false;            // cancelled
    if (typed.trim() !== name) {
      setMsg({ kind: 'err', text: 'Name did not match — delete cancelled.' });
      return false;
    }
    return confirm(
      `Final confirmation — permanently delete "${name}"` +
      (extra ? ` ${extra}` : '') + `?\n\nThis cannot be undone.`,
    );
  };

  const remove = async () => {
    if (!deleteGate(displayName(form), '(this intake record)')) return;
    setBusy('delete'); setMsg(null);
    try { markDirty(false); await deleteIntake(row.id); await onReload(); }
    catch (e) { setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) }); setBusy(null); }
  };

  // Imported rows can't be plain-deleted (importing created a live client +
  // data). Deleting the live client cascades everything AND drops this linked
  // intake row — so this is the real "delete the whole record".
  const removeImported = async () => {
    if (!form.importedClientId) return;
    if (!deleteGate(displayName(form), 'and ALL its data (place, Maps/AI scans, insights) plus this intake record')) return;
    setBusy('delete'); setMsg(null);
    try {
      markDirty(false);
      await deleteClient(form.importedClientId);
      await onReload();
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) });
      setBusy(null);
    }
  };

  // Growable list helper (keywords + emails). Local edits only — Save persists.
  const listField = (key: 'keywords' | 'clientEmails' | 'aiPrompts') => {
    const items = form[key].length ? form[key] : [''];
    return {
      items,
      setItem: (i: number, v: string) => { setForm(f => { const n = [...(f[key].length ? f[key] : [''])]; n[i] = v; return { ...f, [key]: n }; }); markDirty(true); },
      add: () => { setForm(f => ({ ...f, [key]: [...(f[key].length ? f[key] : ['']), ''] })); markDirty(true); },
      remove: (i: number) => { setForm(f => ({ ...f, [key]: (f[key].length ? f[key] : ['']).filter((_, idx) => idx !== i) })); markDirty(true); },
      move: (from: number, to: number) => {
        setForm(f => {
          const arr = [...(f[key].length ? f[key] : [''])];
          if (from < 0 || to < 0 || from >= arr.length || to >= arr.length || from === to) return f;
          const [moved] = arr.splice(from, 1);
          arr.splice(to, 0, moved);
          return { ...f, [key]: arr };
        });
        markDirty(true);
      },
    };
  };
  const kw = listField('keywords');
  const em = listField('clientEmails');
  const ap = listField('aiPrompts');

  const toggleLang = (v: 'en' | 'zh' | 'ms') => {
    const has = form.keywordLanguages.includes(v);
    const next = (has ? form.keywordLanguages.filter(x => x !== v) : [...form.keywordLanguages, v]) as IntakeRow['keywordLanguages'];
    set('keywordLanguages', next);
  };

  const toggleMarket = (v: 'MY' | 'SG') => {
    const has = form.targetMarkets.includes(v);
    const next = (has ? form.targetMarkets.filter(x => x !== v) : [...form.targetMarkets, v]) as IntakeRow['targetMarkets'];
    set('targetMarkets', next);
  };

  const togglePe = (userId: string) => {
    const has = form.projectExecutives.includes(userId);
    set('projectExecutives', has
      ? form.projectExecutives.filter(x => x !== userId)
      : [...form.projectExecutives, userId]);
  };

  // Parent picker (group locations under one client).
  const otherIntakes = allRows.filter(r => r.id !== row.id && !r.parentIntakeId && r.status !== 'archived');
  const candidates = useMemo(() => {
    const q = parentQuery.trim().toLowerCase();
    const fromIntake = otherIntakes.map(r => ({ kind: 'intake' as const, id: r.id, name: displayName(r), sub: r.locationArea || '' }));
    const fromClient = (clients ?? []).filter(c => !c.parentClientId).map(c => ({ kind: 'client' as const, id: c.id, name: c.name, sub: c.location }));
    const pool = [...fromIntake, ...fromClient];
    if (!q) return pool.slice(0, 10);
    return pool.filter(p => p.name.toLowerCase().includes(q) || p.sub.toLowerCase().includes(q)).slice(0, 10);
  }, [otherIntakes, clients, parentQuery]);

  const pickParent = (kind: 'intake' | 'client', id: string) => {
    // Same client = same name: carry the primary's clean name over (editable).
    const inheritedName = kind === 'intake'
      ? (() => { const p = allRows.find(r => r.id === id); return p ? displayName(p) : null; })()
      : (clients?.find(c => c.id === id)?.name ?? null);
    setForm(f => ({
      ...f,
      isBranch: true,
      parentIntakeId: kind === 'intake' ? id : null,
      parentClientId: kind === 'client' ? id : null,
      clientName: inheritedName?.trim() ? inheritedName.trim() : f.clientName,
    }));
    markDirty(true);
  };
  const clearParent = () => {
    setForm(f => ({ ...f, isBranch: false, parentClientId: null, parentIntakeId: null, branchLabel: null }));
    markDirty(true);
  };

  const parentLabel = (() => {
    if (form.parentIntakeId) {
      const p = allRows.find(r => r.id === form.parentIntakeId);
      return p ? `Intake: ${displayName(p)}${p.status !== 'imported' ? ' (not imported yet)' : ''}` : 'Intake (unknown)';
    }
    if (form.parentClientId) {
      const c = clients?.find(x => x.id === form.parentClientId);
      return `Client: ${c?.name ?? form.parentClientId}`;
    }
    return null;
  })();

  const missing: string[] = [];
  if (!form.clientName?.trim()) missing.push('Client name');
  if (!form.gbpName?.trim()) missing.push('GBP name');
  if (!form.locationArea?.trim()) missing.push('Location / area');
  if (!form.placeId?.trim()) missing.push('Place ID');
  if (form.keywords.filter(k => k.trim()).length === 0) missing.push('≥1 keyword');
  const ready = !missing.some(m => m !== 'Client name'); // client name recommended, not blocking import
  const setupReady = !!form.gbpName?.trim()
    && !!form.locationArea?.trim()
    && form.keywords.some(k => k.trim());
  const group = groupMembers(form, allRows);

  return (
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12, padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 18, fontWeight: 700, color: T.fgPrimary }}>
          {displayName(form)}
        </div>
        <GBIBadge color={STATUS_COLOR[form.status]}>{form.status}</GBIBadge>
      </div>
      <div style={{ fontSize: 11, color: dirty ? '#C0790F' : T.fgMuted, marginBottom: 18 }}>
        {saving ? 'Saving…' : dirty ? '● Unsaved changes — click Save to store them.' : 'Edits are kept locally and only stored when you click Save.'}
        {idLock && ' · Imported — name / location / category / country / target markets / keywords / emails stay editable and sync to the live client & its Settings page. Only the GBP identity (full name, map link, Place ID) is locked; change the Place ID from the client’s Settings page.'}
      </div>

      {importResult && <ImportResultPanel res={importResult} onClose={() => setImportResult(null)} />}

      {/* Same-client / second location — chosen FIRST so the client name and
          grouping are set before the rest of the form is filled. */}
      <div style={{
        border: `1px solid ${form.isBranch ? T.cyan : T.border}`, borderRadius: 8,
        padding: '12px 14px', margin: '0 0 18px',
        background: form.isBranch ? 'rgba(18,181,196,0.05)' : '#f8f9fa',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: T.fgPrimary }}>
          <input type="checkbox" checked={form.isBranch}
            onChange={e => (e.target.checked ? set('isBranch', true) : clearParent())} />
          This is another location of the same client
        </label>
        <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 4, lineHeight: 1.5 }}>
          Link this location to its primary one — pick a previous client by name. The client name carries over automatically and they'll share one client card with a location switcher.
        </div>

        {form.isBranch && (
          <div style={{ marginTop: 12 }}>
            {parentLabel && (
              <div style={{
                fontSize: 12, color: T.fgPrimary, padding: '8px 10px', marginBottom: 8,
                background: '#fff', border: `1px solid ${T.cyan}`, borderRadius: 6,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>Primary: <strong>{parentLabel}</strong></span>
                <button type="button" onClick={clearParent} style={{ ...ghostBtn, padding: '3px 8px', fontSize: 11 }}>Change</button>
              </div>
            )}
            {!parentLabel && (
              <>
                <input style={{ ...inp, marginBottom: 6 }}
                  value={parentQuery} onChange={e => setParentQuery(e.target.value)}
                  placeholder="Search the primary client by name…" />
                <div style={{ maxHeight: 170, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {candidates.length === 0 && (
                    <div style={{ fontSize: 11, color: T.fgMuted, fontStyle: 'italic', padding: 6 }}>
                      No match. Create the primary location's record first.
                    </div>
                  )}
                  {candidates.map(c => (
                    <button key={`${c.kind}-${c.id}`} type="button"
                      onClick={() => pickParent(c.kind, c.id)}
                      style={{ textAlign: 'left', padding: '8px 10px', borderRadius: 6, cursor: 'pointer', border: `1px solid ${T.border}`, background: '#fff' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.fgPrimary }}>
                        <span style={{ fontSize: 9, color: T.fgMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 6 }}>{c.kind}</span>
                        {c.name}
                      </div>
                      <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 1 }}>📍 {c.sub}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div style={{ ...labelStyle, marginTop: 12 }}>Branch label</div>
            <input style={{ ...inp, marginTop: 6 }} value={form.branchLabel ?? ''}
              onChange={e => set('branchLabel', e.target.value)}
              placeholder="e.g. Puchong (defaults to Location / area)" />
          </div>
        )}
      </div>

      {/* Client Name — the clean grouping + Card View label */}
      <Field label="Client Name *" hint="Short clean name (e.g. Qi and Acupuncture). Used to group locations and shown on the Clients cards — NOT the long GBP name.">
        <input style={inp} value={form.clientName ?? ''}
          onChange={e => set('clientName', e.target.value)}
          placeholder="Qi and Acupuncture" />
      </Field>

      <Field label="GBP Name (full)" hint="Exactly as shown on Google Maps (can be long / bilingual).">
        <input style={inp} disabled={idLock} value={form.gbpName ?? ''}
          onChange={e => set('gbpName', e.target.value)} />
      </Field>

      <Row>
        <Field label="Location / area *" hint="Structure as Area, State — e.g. Puchong, Selangor. This is the location shown on the dashboard & location switcher; stays editable after import.">
          <input style={inp} value={form.locationArea ?? ''}
            onChange={e => set('locationArea', e.target.value)}
            placeholder="e.g. Puchong, Selangor" />
        </Field>
        <Field label="Country / base" hint="Where the business is physically based / registered. Its exact spot is the Location / area on the left.">
          <select style={inp} value={form.country ?? ''}
            onChange={e => set('country', (e.target.value || null) as IntakeRow['country'])}>
            <option value="">—</option>
            <option value="MY">Malaysia (MY)</option>
            <option value="SG">Singapore (SG)</option>
          </select>
        </Field>
      </Row>

      {/* Target markets — multi-select. A business can serve more than one
          market (e.g. a Johor shop that also wants Singapore customers). */}
      <Field label="Target market(s)" hint="Which market(s) this business wants customers from / wants to be found in. Tick all that apply — a Johor shop serving Singapore too should tick both MY and SG.">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {MARKETS.map(m => {
            const on = form.targetMarkets.includes(m.v);
            return (
              <button key={m.v} type="button" onClick={() => toggleMarket(m.v)} style={{
                padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: sharedStyles.fontBody,
                border: `1px solid ${on ? T.cyan : T.border}`,
                background: on ? 'rgba(18,181,196,0.10)' : '#fff',
                color: on ? T.cyanDim : T.fgSecondary,
              }}>
                {on ? '✓ ' : ''}{m.label}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Google Map link" hint="Paste the maps.app.goo.gl share link — the Place ID auto-fills when you leave this field.">
        <input style={{ ...inp, fontFamily: sharedStyles.fontMono, fontSize: 11 }} disabled={idLock}
          value={form.googleMapLink ?? ''}
          onChange={e => set('googleMapLink', e.target.value)}
          placeholder="https://maps.app.goo.gl/xxxxx" />
      </Field>

      <Field label="Place ID *" hint="Auto-filled from the map link. You can also paste it from the Google Place ID Finder, or click Resolve.">
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...inp, fontFamily: sharedStyles.fontMono, fontSize: 11 }} disabled={idLock}
            value={form.placeId ?? ''}
            onChange={e => set('placeId', e.target.value)}
            placeholder="ChIJ……" />
          <GBIBtn variant="secondary" onClick={resolve} disabled={idLock || busy === 'resolve'}>
            {busy === 'resolve' ? 'Resolving…' : 'Resolve'}
          </GBIBtn>
        </div>
        {(form.placeConfirmName || form.placeConfirmAddress) && (
          <div style={{
            marginTop: 8, padding: '8px 10px', background: 'rgba(18,181,196,0.06)',
            border: `1px solid ${T.cyan}`, borderRadius: 6, fontSize: 11, color: T.fgPrimary,
          }}>
            Confirm this is the right business — <strong>{form.placeConfirmName ?? '—'}</strong>
            {form.placeConfirmAddress ? ` · ${form.placeConfirmAddress}` : ''}
            {form.placeIdSource ? ` (${form.placeIdSource.replace('_', ' ')})` : ''}
          </div>
        )}
      </Field>

      <Field label="Category" hint="e.g. TCM Clinic / Acupuncture">
        <input style={inp} value={form.category ?? ''}
          onChange={e => set('category', e.target.value)} />
      </Field>

      {/* Project Executive(s) — multi-select of manager accounts. Selecting a PE
          auto-grants them access to this client (on import, and live on save for
          an already-imported row); deselecting revokes it. See syncPeGrants. */}
      <Field label="Project executive(s)" hint="YEA team member(s) handling this client. Selected PEs are automatically granted access to this client (on import, or right away if it's already imported) — no manual permission step. Deselecting revokes their access to this client.">
        {peOptions.length === 0 ? (
          <div style={{ fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>No manager accounts found.</div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {peOptions.map(pe => {
              const on = form.projectExecutives.includes(pe.id);
              const label = pe.name?.trim() || pe.email;
              return (
                <button key={pe.id} type="button" onClick={() => togglePe(pe.id)} title={pe.email} style={{
                  padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, fontFamily: sharedStyles.fontBody,
                  border: `1px solid ${on ? T.cyan : T.border}`,
                  background: on ? 'rgba(18,181,196,0.10)' : '#fff',
                  color: on ? T.cyanDim : T.fgSecondary,
                }}>
                  {on ? '✓ ' : ''}{label}
                </button>
              );
            })}
          </div>
        )}
      </Field>

      {/* Client login emails — growable, multiple contacts. Hidden for branch
          locations: one login on the PRIMARY account already sees the whole
          group, so we never add emails on the extra locations. */}
      {form.isBranch ? (
        <div style={{
          marginTop: 6, marginBottom: 16, padding: '10px 12px', borderRadius: 8,
          background: '#f8f9fa', border: `1px solid ${T.border}`, fontSize: 11, color: T.fgMuted, lineHeight: 1.5,
        }}>
          🔒 Login emails are managed on the <strong>primary</strong> client only — one login sees every location of this client. No emails are added on extra locations.
        </div>
      ) : (
        <>
          <div style={{ ...labelStyle, marginTop: 6, marginBottom: 6 }}>
            Client login emails <span style={{ color: T.fgMuted, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional, add one per person who needs to log in</span>
          </div>
          {em.items.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input style={inp} type="email" value={e}
                onChange={ev => em.setItem(i, ev.target.value)}
                placeholder={i === 0 ? 'owner@business.com (add later if unknown)' : 'another contact'} />
              {em.items.length > 1 && (
                <button type="button" onClick={() => em.remove(i)} title="Remove" style={iconBtn}>✕</button>
              )}
            </div>
          ))}
          <button type="button" onClick={em.add} style={{ ...ghostBtn, marginBottom: 16 }}>+ Add email</button>
        </>
      )}

      {/* Keyword languages — multi-select */}
      <Field label="Keyword language (select all that apply)">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {LANGS.map(l => {
            const on = form.keywordLanguages.includes(l.v);
            return (
              <button key={l.v} type="button" onClick={() => toggleLang(l.v)} style={{
                padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: sharedStyles.fontBody,
                border: `1px solid ${on ? T.cyan : T.border}`,
                background: on ? 'rgba(18,181,196,0.10)' : '#fff',
                color: on ? T.cyanDim : T.fgSecondary,
              }}>
                {on ? '✓ ' : ''}{l.label}
              </button>
            );
          })}
        </div>
      </Field>

      {/* Keywords — growable list (also editable after import for LF setup) */}
      <div style={{ ...labelStyle, marginTop: 6, marginBottom: 8 }}>
        Keywords to track * <span style={{ color: T.fgMuted, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— add as many as you need (your Local Falcon reference list)</span>
      </div>

      {/* How many of the (reordered) keywords actually get scheduled into the LF
          campaign. Default 3; raising it costs more LF credits per run. The
          divider below + the campaign preview both follow this number. */}
      {(() => {
        const filled = kw.items.filter(x => x.trim()).length;
        const effN = Math.min(form.lfTrackCount, Math.max(filled, 1));
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            margin: '0 0 12px', padding: '8px 12px', borderRadius: 8,
            background: 'rgba(18,181,196,0.06)', border: `1px solid ${T.cyan}`,
            fontSize: 12, color: T.fgSecondary,
          }}>
            <span style={{ fontWeight: 600, color: T.fgPrimary }}>📍 Schedule top</span>
            <input
              type="number" min={1} max={20} value={form.lfTrackCount}
              onChange={e => {
                const n = Math.round(Number(e.target.value));
                set('lfTrackCount', Number.isFinite(n) ? Math.min(Math.max(n, 1), 20) : 3);
              }}
              style={{ ...inp, width: 60, textAlign: 'center', padding: '6px 8px' }}
            />
            <span>keyword{form.lfTrackCount === 1 ? '' : 's'} into Local Falcon · ≈ <b style={{ color: T.fgPrimary }}>{effN * 4 * 9} credits</b> / run</span>
          </div>
        );
      })()}

      {kw.items.map((k, i) => (
        <Fragment key={i}>
          <div
            draggable
            onDragStart={() => setKwDrag(i)}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); if (kwDrag !== null) kw.move(kwDrag, i); setKwDrag(null); }}
            onDragEnd={() => setKwDrag(null)}
            style={{
              display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center',
              opacity: kwDrag === i ? 0.4 : 1,
              outline: kwDrag !== null && kwDrag !== i ? `1px dashed ${T.cyan}` : 'none',
              outlineOffset: 2, borderRadius: 6,
            }}>
            <span title="Drag to reorder" style={{ cursor: 'grab', color: T.fgMuted, fontSize: 14, userSelect: 'none', lineHeight: 1 }}>⠿</span>
            <span style={{ fontSize: 11, color: i < form.lfTrackCount ? T.cyanDim : T.fgMuted, fontWeight: i < form.lfTrackCount ? 700 : 400, width: 18, textAlign: 'right' }}>{i + 1}</span>
            <input style={inp} value={k}
              onChange={e => kw.setItem(i, e.target.value)}
              placeholder={i === 0 ? 'e.g. tcm johor bahru' : ''} />
            {kw.items.length > 1 && (
              <button type="button" onClick={() => kw.remove(i)} title="Remove" style={iconBtn}>✕</button>
            )}
          </div>
          {i === form.lfTrackCount - 1 && kw.items.filter(x => x.trim()).length > form.lfTrackCount && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 10px 26px', fontSize: 11, color: T.cyanDim, fontWeight: 600 }}>
              <span style={{ flex: 1, height: 1, background: T.cyan, opacity: 0.4 }} />
              top {form.lfTrackCount} scheduled into Local Falcon ↑ · drag your best {form.lfTrackCount} above
              <span style={{ flex: 1, height: 1, background: T.border }} />
            </div>
          )}
        </Fragment>
      ))}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button type="button" onClick={kw.add} style={ghostBtn}>+ Add keyword</button>
        {form.status === 'imported' && form.importedClientId && (
          <button type="button" onClick={reconcileFromLf} disabled={busy === 'reconcile'}
            style={{ ...ghostBtn, color: T.cyanDim, borderColor: T.cyan }}
            title="Add any keywords / AI prompts Local Falcon is actually tracking that aren't in this list yet">
            {busy === 'reconcile' ? 'Checking Local Falcon…' : '↺ Pull from Local Falcon'}
          </button>
        )}
      </div>

      {/* AI prompts — same as keywords by default; uncheck to track a
          separate AI Scan prompt list (also exported for Local Falcon). */}
      <div style={{
        border: `1px solid ${form.aiPromptsSame ? T.border : T.cyan}`, borderRadius: 8,
        padding: '12px 14px', margin: '0 0 16px',
        background: form.aiPromptsSame ? '#f8f9fa' : 'rgba(18,181,196,0.05)',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: T.fgPrimary }}>
          <input type="checkbox" checked={form.aiPromptsSame}
            onChange={e => set('aiPromptsSame', e.target.checked)} />
          AI Prompt same as keyword
        </label>
        <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 4, lineHeight: 1.5 }}>
          On (default): the AI Scan prompts are exactly the keywords above. Uncheck to collect a separate AI prompt list — it’s saved with the record and exported for your Local Falcon AI setup.
        </div>

        {!form.aiPromptsSame && (
          <div style={{ marginTop: 12 }}>
            <div style={{ ...labelStyle, marginBottom: 8 }}>
              AI prompts to track <span style={{ color: T.fgMuted, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— the prompts you’ll configure for AI Scans in Local Falcon</span>
            </div>
            {ap.items.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: T.fgMuted, width: 18, textAlign: 'right' }}>{i + 1}</span>
                <input style={inp} value={p}
                  onChange={e => ap.setItem(i, e.target.value)}
                  placeholder={i === 0 ? 'e.g. best tcm clinic near me' : ''} />
                {ap.items.length > 1 && (
                  <button type="button" onClick={() => ap.remove(i)} title="Remove" style={iconBtn}>✕</button>
                )}
              </div>
            ))}
            <button type="button" onClick={ap.add} style={{ ...ghostBtn }}>+ Add AI prompt</button>
          </div>
        )}
      </div>

      <Field label="Notes (internal)">
        <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={form.notes ?? ''}
          onChange={e => set('notes', e.target.value)} />
      </Field>

      {form.status !== 'imported' && (
        <div style={{
          marginTop: 6, padding: '10px 12px', borderRadius: 8,
          background: ready ? 'rgba(30,158,90,0.08)' : 'rgba(245,160,32,0.10)',
          border: `1px solid ${ready ? '#1E9E5A' : '#F5A020'}`, fontSize: 12, color: T.fgPrimary,
        }}>
          {ready
            ? `Ready for automation. Save & Start Setup creates/repairs the account, campaign, scans, AI Visibility and report.${!form.clientName?.trim() ? ' (Tip: set a Client Name for a tidy Card View.)' : ''}`
            : `Still needed: ${missing.filter(m => m !== 'Client name' && m !== 'Place ID').join(', ')}${!form.placeId ? ' · Place ID will be resolved automatically' : ''}`}
        </div>
      )}

      {/* Local Falcon Campaign — Phase 1: create (paused) → review/re-center on LF → Start.
          Shown for draft/ready/imported rows (NOT archived). Imported rows are the
          main case: you convert the client first, then schedule its LF campaign. */}
      {recoveryMode && form.status !== 'archived' && (ready || form.lfCampaignKey) && (() => {
        const topKw = form.keywords.map(k => k.trim()).filter(Boolean).slice(0, form.lfTrackCount);
        const creditsEst = topKw.length * 1 * 9;   // keywords × 1 platform (google/Maps only) × 9 grid points
        const started = form.lfCampaignStatus === 'started';
        // Drift detection: LF has no auto-sync, so compare the row's current
        // top-N against the snapshot pushed to LF at create time. Match the
        // backend normalizeKeyword (NFC, strip invisibles, NBSP→space) so the
        // comparison is byte-stable, then lowercase for case-insensitivity.
        const normKw = (s: string) => s
          .normalize('NFC')
          .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/g, '')
          .replace(/\u00A0/g, ' ')
          .replace(/\s+/g, ' ')
          .trim().toLowerCase();
        const lfSnapshot = (form.lfCampaignKeywords ?? []).filter(k => k.trim());
        const lfSet = new Set(lfSnapshot.map(normKw).filter(Boolean));
        const curSet = new Set(topKw.map(normKw).filter(Boolean));
        const outOfSync = !!form.lfCampaignKey && lfSnapshot.length > 0 &&
          (lfSet.size !== curSet.size || [...curSet].some(k => !lfSet.has(k)));
        const notYetOnLf = topKw.filter(k => !lfSet.has(normKw(k)));
        const droppedHere = lfSnapshot.filter(k => !curSet.has(normKw(k)));
        return (
          <div style={{ marginTop: 14, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px', background: '#fafbfc' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.fgPrimary, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              📍 Local Falcon Campaign
              {form.lfCampaignKey && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 10,
                  background: started ? 'rgba(30,158,90,0.12)' : 'rgba(245,160,32,0.15)',
                  color: started ? '#15703f' : '#9a6700',
                  border: `1px solid ${started ? '#1E9E5A' : '#F5A020'}`,
                }}>{started ? 'Started · runs biweekly' : 'Created · paused'}</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: T.fgSecondary, lineHeight: 1.6, marginBottom: 10 }}>
              Tracks the top {topKw.length} keyword{topKw.length === 1 ? '' : 's'}: <b style={{ color: T.fgPrimary }}>{topKw.join(' · ') || '—'}</b><br />
              3×3 grid · 6.0&nbsp;mi · every 2 weeks · Google (Maps) · <b style={{ color: T.fgPrimary }}>≈ {creditsEst} credits / run</b>
            </div>
            {outOfSync && (
              <div style={{
                marginBottom: 10, padding: '9px 12px', borderRadius: 8,
                background: 'rgba(214,69,69,0.08)', border: '1px solid #d64545',
                fontSize: 12, color: '#8a2b2b', lineHeight: 1.6,
              }}>
                <b>⚠️ Keywords changed — Local Falcon is out of sync.</b><br />
                Local Falcon has no auto-sync: this campaign still runs the keywords it was created with. Your edits here do <b>not</b> reach LF until you update them there.
                <div style={{ marginTop: 5 }}>
                  Local Falcon now: <b>{lfSnapshot.join(' · ') || '—'}</b><br />
                  Current top {form.lfTrackCount}: <b>{topKw.join(' · ') || '—'}</b>
                </div>
                {(notYetOnLf.length > 0 || droppedHere.length > 0) && (
                  <div style={{ marginTop: 5 }}>
                    {notYetOnLf.length > 0 && <span>➕ Not yet on LF: <b>{notYetOnLf.join(' · ')}</b><br /></span>}
                    {droppedHere.length > 0 && <span>➖ Still on LF, removed here: <b>{droppedHere.join(' · ')}</b></span>}
                  </div>
                )}
                <div style={{ marginTop: 5 }}>→ Open <b>↗ Review on Local Falcon</b> below and edit the campaign's keywords to match. The snapshot updates only when a campaign is (re)created here.</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {!form.lfCampaignKey ? (
                <GBIBtn variant="primary" onClick={doCreateCampaign} disabled={!ready || busy === 'campaign'}>
                  {busy === 'campaign' ? 'Creating…' : '① Create campaign (paused)'}
                </GBIBtn>
              ) : (
                <>
                  <a href={`https://www.localfalcon.com/campaigns/edit/${form.lfCampaignKey}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <GBIBtn variant="secondary">↗ Review on Local Falcon</GBIBtn>
                  </a>
                  <GBIBtn variant="secondary" onClick={doSyncKeywords} disabled={busy === 'campaign-sync'}>
                    {busy === 'campaign-sync' ? 'Pushing…' : '↑ Push my keywords to Local Falcon'}
                  </GBIBtn>
                  {!started && (
                    <GBIBtn variant="primary" onClick={doStartCampaign} disabled={busy === 'campaign-start'}>
                      {busy === 'campaign-start' ? 'Starting…' : '② Start campaign'}
                    </GBIBtn>
                  )}
                </>
              )}
            </div>
            {form.lfCampaignKey && !started && (
              <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 8, lineHeight: 1.5 }}>
                Paused — open Local Falcon to drag the map and re-center the 3×3 grid over the client's area, double-check it, then press <b>Start</b>.
              </div>
            )}
          </div>
        );
      })()}

      {setupJob && (
        <div style={{ marginTop: 14, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px', background: '#fafbfc' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.fgPrimary, marginBottom: 10 }}>
            Automation status · {setupJob.status.replace(/_/g, ' ')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(125px, 1fr))', gap: 7 }}>
            {setupJob.steps.map(step => (
              <div key={step.key} style={{
                padding: '8px 9px', borderRadius: 7, fontSize: 11,
                border: `1px solid ${step.status === 'failed' ? '#E8501A' : step.status === 'completed' ? '#1E9E5A' : T.border}`,
                background: '#fff', color: T.fgPrimary,
              }}>
                <div style={{ fontWeight: 700 }}>{step.key.replace(/_/g, ' ')}</div>
                <div style={{ marginTop: 2, color: T.fgMuted }}>{step.status}{step.attempts ? ` · attempt ${step.attempts}` : ''}</div>
              </div>
            ))}
          </div>
          {setupJob.error && (
            <div style={{ marginTop: 9, fontSize: 11, color: '#C03B0F' }}>
              {setupJob.error.code}: {setupJob.error.message}
            </div>
          )}
        </div>
      )}

      {msg && (
        <div style={{
          marginTop: 12, padding: '9px 12px', borderRadius: 6, fontSize: 12,
          background: msg.kind === 'ok' ? 'rgba(30,158,90,0.08)' : 'rgba(232,80,26,0.08)',
          border: `1px solid ${msg.kind === 'ok' ? '#1E9E5A' : '#E8501A'}`,
          color: msg.kind === 'ok' ? '#15703f' : '#C03B0F',
        }}>{msg.text}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Admins always get a delete path. Imported + a live client →
              cascade the client; otherwise (draft, or an orphaned import with
              no linked client) just delete the intake row. */}
          {isAdmin && (
            form.status === 'imported' && form.importedClientId ? (
              <button onClick={removeImported} disabled={busy === 'delete'}
                style={{ ...ghostBtn, color: '#C03B0F', borderColor: '#E8501A55' }}
                title="Permanently delete the live client + all its data + this intake record">
                {busy === 'delete' ? 'Deleting…' : '🗑 Delete client + record'}
              </button>
            ) : (
              <button onClick={remove} disabled={busy === 'delete'}
                style={{ ...ghostBtn, color: '#C03B0F', borderColor: '#E8501A55' }}
                title="Permanently delete this intake record">
                {busy === 'delete' ? 'Deleting…' : '🗑 Delete record'}
              </button>
            )
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 11, color: '#C0790F', fontWeight: 600 }}>● Unsaved</span>}
          {!canStartSetup && <button onClick={() => saveAll()} disabled={!dirty || saving}
            style={{
              background: dirty ? '#1E7FD4' : '#e8eaed',
              color: dirty ? '#fff' : T.fgMuted,
              border: 'none', borderRadius: 8, padding: '9px 18px',
              cursor: dirty && !saving ? 'pointer' : 'default',
              fontSize: 13, fontWeight: 700, fontFamily: sharedStyles.fontBody,
            }}>
            {saving ? 'Saving…' : dirty ? 'Save draft' : 'Saved'}
          </button>}
          <GBIBtn variant="secondary" onClick={() => exportClientCsv(form, allRows)}>
            ⬇ Export CSV (this client · {group.length} location{group.length > 1 ? 's' : ''})
          </GBIBtn>
          {canStartSetup && form.status !== 'archived' && (
            <GBIBtn variant="primary" onClick={doStartSetup} disabled={!setupReady || busy === 'setup'}>
              {busy === 'setup'
                ? 'Starting automation…'
                : form.status === 'imported' ? 'Save Changes & Update' : 'Save & Start Setup'}
            </GBIBtn>
          )}
          {recoveryMode && form.status !== 'imported' && (
            <GBIBtn variant="secondary" onClick={doImport} disabled={!ready || busy === 'import'}>
              {busy === 'import' ? 'Converting…' : 'Recovery: Convert only'}
            </GBIBtn>
          )}
          {form.status === 'imported' && form.importedClientId ? (
            <Link to={`/admin/clients/${form.importedClientId}`} style={{ textDecoration: 'none' }}>
              <GBIBtn variant="secondary">Open client dashboard →</GBIBtn>
            </Link>
          ) : !canStartSetup && (
            <span style={{ fontSize: 11, color: T.fgMuted }}>Staff can save drafts; Admin/Manager starts automation.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ImportResultPanel({ res, onClose }: { res: ImportIntakeResponse; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 18, padding: 16, borderRadius: 10, background: 'rgba(30,158,90,0.07)', border: '1px solid #1E9E5A' }}>
      <div style={{ fontWeight: 700, color: '#15703f', marginBottom: 6 }}>✓ Live client account created</div>
      {res.logins.length > 0 ? (
        <div style={{ fontSize: 12, color: T.fgPrimary, marginBottom: 10 }}>
          Logins: {res.logins.map(l => `${l.email} (${l.status})`).join(' · ')}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: T.fgPrimary, marginBottom: 10 }}>
          No email yet — no login created. Add emails on this record later; they'll be provisioned automatically.
        </div>
      )}
      {res.magicLink && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <input readOnly value={res.magicLink} style={{ ...inp, fontFamily: sharedStyles.fontMono, fontSize: 11 }} />
          <GBIBtn variant="secondary" onClick={() => { navigator.clipboard?.writeText(res.magicLink!); setCopied(true); }}>
            {copied ? 'Copied' : 'Copy link'}
          </GBIBtn>
        </div>
      )}
      <div style={{ fontSize: 11, color: T.fgSecondary }}>
        Pipeline: {res.steps.map(s => `${s.step}:${s.status}`).join(' · ') || '—'}
      </div>
      <button onClick={onClose} style={{ ...ghostBtn, marginTop: 10, fontSize: 11, padding: '4px 10px' }}>Dismiss</button>
    </div>
  );
}

// ── Tab 2 — Data view ──────────────────────────────────────────

function DataTab({ rows, onOpen }: { rows: IntakeRow[]; onOpen: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | IntakeRow['status']>('');

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!s) return true;
      return [r.clientName, r.gbpName, r.locationArea, ...r.clientEmails, r.placeId, ...r.keywords]
        .some(v => v?.toLowerCase().includes(s));
    });
  }, [rows, q, statusFilter]);

  const exportAll = () => {
    const head = ['Status', 'Client Name', 'GBP Name', 'Location', 'Country', 'Target Markets', 'Emails', 'Place ID', 'Keywords', 'Languages', 'AI Prompts', 'Same client', 'Updated'];
    const lines = filtered.map(r => [
      r.status, r.clientName, r.gbpName, r.locationArea, r.country, r.targetMarkets.join(' | '), r.clientEmails.join(' | '), r.placeId,
      r.keywords.join(' | '), r.keywordLanguages.map(l => LANG_LABEL[l] ?? l).join(' | '),
      (r.aiPromptsSame ? r.keywords : r.aiPrompts).join(' | '),
      r.parentIntakeId ? (rows.find(x => x.id === r.parentIntakeId) ? displayName(rows.find(x => x.id === r.parentIntakeId)!) : r.parentIntakeId) : (r.parentClientId ?? ''),
      new Date(r.updatedAt).toISOString(),
    ].map(csvEscape).join(','));
    const blob = new Blob([[head.map(csvEscape).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `client-intake-all-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by client / GBP / email / keyword / Place ID…"
          style={{ ...inp, maxWidth: 380 }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as '' | IntakeRow['status'])} style={{ ...inp, maxWidth: 160 }}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="ready">Ready</option>
          <option value="imported">Imported</option>
          <option value="archived">Archived</option>
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={exportAll} style={ghostBtn}>⬇ Export all (overview)</button>
      </div>

      {/* Spreadsheet-style: fixed column widths, every row the same height,
          long values clip to one line (full text on hover), the whole table
          scrolls horizontally and the Status + Client Name columns stay pinned. */}
      <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 10, background: '#fff' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed', width: DV_TABLE_W }}>
          <colgroup>
            <col style={{ width: DV_COLW.status }} />
            <col style={{ width: DV_COLW.name }} />
            <col style={{ width: DV_COLW.loc }} />
            <col style={{ width: DV_COLW.country }} />
            <col style={{ width: DV_COLW.markets }} />
            <col style={{ width: DV_COLW.emails }} />
            <col style={{ width: DV_COLW.kw }} />
            <col style={{ width: DV_COLW.lang }} />
            <col style={{ width: DV_COLW.place }} />
            <col style={{ width: DV_COLW.same }} />
            <col style={{ width: DV_COLW.act }} />
            <col style={{ width: DV_COLW.dash }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
              <th style={{ ...th, ...stickyCol(0, '#f8f9fa', 3) }}>Status</th>
              <th style={{ ...th, ...stickyCol(DV_COLW.status, '#f8f9fa', 3) }}>Client Name</th>
              <th style={th}>Location</th>
              <th style={th}>Country</th>
              <th style={th}>Markets</th>
              <th style={th}>Emails</th>
              <th style={th}>Keywords</th>
              <th style={th}>Language</th>
              <th style={th}>Place ID</th>
              <th style={th}>Same client</th>
              <th style={th} />
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={12} style={{ ...td, whiteSpace: 'normal', color: T.fgMuted, fontStyle: 'italic' }}>No records.</td></tr>
            )}
            {filtered.map(r => {
              const parent = r.parentIntakeId ? rows.find(x => x.id === r.parentIntakeId) : null;
              const sameClient = r.parentIntakeId ? (parent ? displayName(parent) : 'intake') : (r.parentClientId ? 'client' : '—');
              const nameTxt = `${r.parentIntakeId ? '↳ ' : ''}${displayName(r)}`;
              const kwFull = r.keywords.join(' · ');
              // One login covers the whole client group — a branch with no own
              // email still shows the group's login (from its primary) so the
              // contact is never invisible in the sheet.
              const emailSrc = r.clientEmails.length
                ? r
                : (parent ?? (r.parentClientId ? rows.find(x => x.importedClientId === r.parentClientId) ?? null : null));
              const groupEmails = emailSrc ? emailSrc.clientEmails : [];
              const emInherited = r.clientEmails.length === 0 && groupEmails.length > 0;
              const emFull = groupEmails.join(', ');
              const langTxt = r.keywordLanguages.map(l => LANG_LABEL[l] ?? l).join('/');
              return (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={{ ...td, ...stickyCol(0, '#fff', 1) }}><GBIBadge color={STATUS_COLOR[r.status]}>{r.status}</GBIBadge></td>
                  <td title={nameTxt} style={{ ...td, ...stickyCol(DV_COLW.status, '#fff', 1), fontWeight: 600, color: T.fgPrimary }}>{nameTxt}</td>
                  <td title={r.locationArea || ''} style={td}>{r.locationArea || '—'}</td>
                  <td style={td}>{r.country || '—'}</td>
                  <td title={r.targetMarkets.join(', ')} style={td}>{r.targetMarkets.length ? r.targetMarkets.join('/') : '—'}</td>
                  <td title={emInherited ? `Shared group login (from ${parent ? displayName(parent) : 'primary'}) — ${emFull}` : emFull} style={td}>
                    {groupEmails.length
                      ? <span style={emInherited ? { color: T.fgMuted } : undefined}>{emFull}{emInherited && <span style={{ fontSize: 9, color: T.fgMuted }}> (group)</span>}</span>
                      : <span style={{ color: T.fgMuted }}>—</span>}
                  </td>
                  <td title={kwFull} style={td}>{kwFull || '—'}</td>
                  <td title={langTxt} style={td}>{langTxt || '—'}</td>
                  <td title={r.placeId || ''} style={{ ...td, fontFamily: sharedStyles.fontMono, fontSize: 10 }}>
                    {r.placeId ? `${r.placeId.slice(0, 16)}…` : <span style={{ color: T.amber }}>none</span>}
                  </td>
                  <td title={sameClient} style={td}>{sameClient}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => onOpen(r.id)} style={{ ...ghostBtn, padding: '4px 10px', fontSize: 11 }}>Open</button>
                      <button onClick={() => exportClientCsv(r, rows)} style={{ ...ghostBtn, padding: '4px 10px', fontSize: 11 }} title="Export this client (+ branches) for Local Falcon">⬇ CSV</button>
                    </div>
                  </td>
                  <td style={td}>
                    {r.status === 'imported' && r.importedClientId && (
                      <Link to={`/admin/clients/${r.importedClientId}`} style={{ textDecoration: 'none' }}>
                        <button style={{ ...ghostBtn, padding: '4px 10px', fontSize: 11 }}>Dashboard</button>
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────

// ── Tab 3 — Reconcile (LF vs Dashboard) ───────────────────────
const VERDICT: Record<ReconcileRow['verdict'], { label: string; color: string; bg: string }> = {
  ok:           { label: '✓ OK',           color: '#15703f', bg: 'rgba(30,158,90,0.10)' },
  not_synced:   { label: '⚠ Not synced',   color: '#9a6700', bg: 'rgba(245,160,32,0.12)' },
  broken_link:  { label: '✕ Broken link',  color: '#b3261e', bg: 'rgba(214,69,69,0.10)' },
  no_campaign:  { label: '○ No campaign',  color: '#9a6700', bg: 'rgba(245,160,32,0.12)' },
  not_imported: { label: '○ Not imported', color: '#5f6368', bg: '#f1f3f4' },
  no_place:     { label: '○ No Place ID',  color: '#5f6368', bg: '#f1f3f4' },
};
const VERDICT_ORDER: ReconcileRow['verdict'][] = ['broken_link', 'no_campaign', 'not_imported', 'not_synced', 'no_place', 'ok'];

function ReconcileTab({ onOpen }: { onOpen: (id: string) => void }) {
  const [data, setData] = useState<{ lfError: string | null; rows: ReconcileRow[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    reconcileIntake()
      .then(r => { if (!cancelled) { setData({ lfError: r.lfError, rows: r.rows }); setErr(null); } })
      .catch(e => { if (!cancelled) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: T.fgMuted, fontSize: 13 }}>Checking Local Falcon…</div>;
  if (err) return <ErrorBox msg={err} />;
  if (!data) return null;

  const counts = data.rows.reduce<Record<string, number>>((a, r) => { a[r.verdict] = (a[r.verdict] ?? 0) + 1; return a; }, {});
  const sorted = [...data.rows].sort((a, b) => VERDICT_ORDER.indexOf(a.verdict) - VERDICT_ORDER.indexOf(b.verdict));

  return (
    <div>
      {data.lfError && <ErrorBox msg={`Local Falcon check failed: ${data.lfError} — the "Campaign" column may be inaccurate.`} />}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {VERDICT_ORDER.filter(v => counts[v]).map(v => (
          <span key={v} style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, color: VERDICT[v].color, background: VERDICT[v].bg }}>{VERDICT[v].label}: {counts[v]}</span>
        ))}
      </div>
      <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 0.6fr auto', gap: 12, padding: '10px 16px', fontSize: 11, fontWeight: 700, color: T.fgMuted, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}`, background: '#f8f9fa' }}>
          <div>Client</div><div>Campaign</div><div>Maps synced</div><div>AI</div><div>Status</div>
        </div>
        {sorted.map(r => (
          <div key={r.id} onClick={() => onOpen(r.id)} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 0.6fr auto', gap: 12, alignItems: 'center', padding: '10px 16px', borderTop: `1px solid ${T.border}`, cursor: 'pointer', fontSize: 13 }}
            onMouseEnter={e => { e.currentTarget.style.background = T.bgSubtle; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: T.fgPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || '(unnamed)'}</div>
              <div style={{ fontSize: 11, color: T.fgMuted }}>{r.location}</div>
            </div>
            <div style={{ fontSize: 12, color: r.brokenLink ? '#b3261e' : r.campaignExists ? T.fgSecondary : T.fgMuted }}>
              {!r.campaignKey ? '—' : r.brokenLink ? 'deleted in LF' : r.campaignExists ? 'live' : '?'}
            </div>
            <div style={{ fontSize: 12, color: T.fgSecondary }}>{r.imported ? (r.mapsKeywords > 0 ? `${r.mapsKeywords} kw · ${r.lastScan ? new Date(r.lastScan).toLocaleDateString() : ''}` : 'none') : '—'}</div>
            <div style={{ fontSize: 12, color: T.fgSecondary }}>{r.imported ? (r.aiCount > 0 ? String(r.aiCount) : 'none') : '—'}</div>
            <div><span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 8, color: VERDICT[r.verdict].color, background: VERDICT[r.verdict].bg, whiteSpace: 'nowrap' }}>{VERDICT[r.verdict].label}</span></div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 10, lineHeight: 1.6 }}>
        <b>Broken link</b> = points to a campaign deleted in Local Falcon → recreate. <b>No campaign</b> = imported but none created. <b>Not imported</b> = convert to client first. <b>Not synced</b> = press Sync. Click a row to open it.
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ marginTop: 6 }}>{children}</div>
      {hint && <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 4, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}
function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>{children}</div>;
}
function ErrorBox({ msg }: { msg: string }) {
  return (
    <div style={{
      marginBottom: 16, padding: '10px 12px', background: 'rgba(232,80,26,0.08)',
      border: '1px solid #E8501A', borderRadius: 6, fontSize: 12, color: '#C03B0F',
    }}>{msg}</div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: T.fgSecondary,
};
const inp: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 13,
  fontFamily: sharedStyles.fontBody, color: T.fgPrimary,
  background: '#fff', border: `1px solid ${T.border}`, borderRadius: 6,
  outline: 'none', boxSizing: 'border-box',
};
const ghostBtn: React.CSSProperties = {
  background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8,
  padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: T.fgSecondary,
  fontFamily: sharedStyles.fontBody,
};
const iconBtn: React.CSSProperties = {
  border: `1px solid ${T.border}`, background: '#fff', borderRadius: 6,
  width: 32, height: 32, cursor: 'pointer', color: '#C03B0F', flexShrink: 0,
};
// Fixed column widths → uniform rows + horizontal scroll (Sheets/Monday feel).
const DV_COLW = {
  status: 96, name: 200, loc: 150, country: 70, markets: 90, emails: 210,
  kw: 280, lang: 116, place: 140, same: 140, act: 150, dash: 110,
} as const;
const DV_TABLE_W = Object.values(DV_COLW).reduce((a, b) => a + b, 0);
// Pin a column while the rest scrolls horizontally underneath it.
const stickyCol = (left: number, bg: string, z: number): React.CSSProperties => ({
  position: 'sticky', left, background: bg, zIndex: z,
});
const th: React.CSSProperties = {
  padding: '10px 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
  textTransform: 'uppercase', color: T.fgSecondary,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box',
};
const td: React.CSSProperties = {
  padding: '9px 12px', color: T.fgSecondary, verticalAlign: 'middle', height: 44,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box',
};
